import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Library, Workflow, ShieldCheck, UserPlus, UserMinus, ArrowRight, DatabaseZap, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Platform() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPA = user?.role === "platform_admin";
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [counts, setCounts] = useState({ techniques: 0, sequences: 0 });
  const [busy, setBusy] = useState(false);
  const [demoteId, setDemoteId] = useState(null);
  const [seedOpen, setSeedOpen] = useState(false);
  const [seedPreview, setSeedPreview] = useState(null);
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  useEffect(() => { if (user && !isPA) navigate("/dashboard", { replace: true }); }, [user, isPA, navigate]);

  const load = useCallback(async () => {
    try {
      const [a, t, s] = await Promise.all([api.get("/platform/admins"), api.get("/techniques"), api.get("/sequences")]);
      setAdmins(a.data);
      setCounts({ techniques: t.data.length, sequences: s.data.length });
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { if (isPA) load(); }, [isPA, load]);

  const promote = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try { await api.post("/platform/admins", { email: email.trim() }); toast.success("Platform admin added"); setEmail(""); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const demote = async () => {
    try { await api.delete(`/platform/admins/${demoteId}`); toast.success("Platform admin removed"); setDemoteId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openSeedDialog = async () => {
    setSeedOpen(true);
    setSeedResult(null);
    setSeedPreview(null);
    try {
      const { data } = await api.post("/platform/seed-content?dry_run=true");
      setSeedPreview(data);
    } catch (e) { toast.error(apiError(e)); setSeedOpen(false); }
  };

  const runSeed = async (overwrite) => {
    setSeedBusy(true);
    try {
      const { data } = await api.post(`/platform/seed-content${overwrite ? "?overwrite=true" : ""}`);
      setSeedResult(data);
      toast.success(`Imported ${data.techniques.inserted + data.techniques.updated} techniques, ${data.sequences.inserted + data.sequences.updated} sequences`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSeedBusy(false); }
  };

  if (!isPA) return null;

  return (
    <div className="p-6 md:p-8 pb-24 md:pb-8 max-w-4xl" data-testid="platform-page">
      <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-1">🌐 Platform</p>
      <h1 className="font-heading font-bold text-3xl tracking-tight">Platform Management</h1>
      <p className="text-muted-foreground text-sm mt-2 max-w-xl">
        Everything you create here is part of the platform library and is visible to every gym, member and individual on Infinite Sequence.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <button data-testid="platform-techniques-card" onClick={() => navigate("/library")}
          className="group rounded-xl bg-card border border-border hover:border-primary/60 transition-colors p-5 text-left">
          <div className="flex items-center justify-between">
            <Library className="h-6 w-6 text-primary" />
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <p className="font-heading font-bold text-3xl mt-4">{counts.techniques}</p>
          <p className="text-sm text-muted-foreground">Platform techniques</p>
        </button>
        <button data-testid="platform-sequences-card" onClick={() => navigate("/sequences")}
          className="group rounded-xl bg-card border border-border hover:border-primary/60 transition-colors p-5 text-left">
          <div className="flex items-center justify-between">
            <Workflow className="h-6 w-6 text-primary" />
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <p className="font-heading font-bold text-3xl mt-4">{counts.sequences}</p>
          <p className="text-sm text-muted-foreground">Platform sequences</p>
        </button>
      </div>

      <div className="mt-10">
        <h2 className="font-heading font-semibold text-xl flex items-center gap-2">
          <DatabaseZap className="h-5 w-5 text-primary" /> Curriculum Seed
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Import the bundled 72 platform techniques and 8 platform sequences into this environment. Safe to run repeatedly — existing items are skipped by default.</p>
        <Button data-testid="open-seed-dialog-btn" onClick={openSeedDialog} className="mt-4 bg-primary hover:bg-primary/90 text-white">
          <DatabaseZap className="h-4 w-4 mr-1.5" /> Import platform curriculum
        </Button>
      </div>

      <div className="mt-10">
        <h2 className="font-heading font-semibold text-xl flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" /> Platform Admins
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Only platform admins can grant this role. The user must already have an account.</p>

        <div className="flex gap-2 mt-4 max-w-md">
          <Input data-testid="promote-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") promote(); }}
            placeholder="user@email.com" className="bg-card border-border" />
          <Button data-testid="promote-admin-btn" onClick={promote} disabled={busy} className="bg-primary hover:bg-primary/90 text-white shrink-0">
            <UserPlus className="h-4 w-4 mr-1.5" /> Add admin
          </Button>
        </div>

        <div className="mt-5 space-y-2 max-w-2xl">
          {admins.map((a) => (
            <div key={a.user_id} data-testid={`platform-admin-row-${a.user_id}`}
              className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border">
              <Avatar className="h-9 w-9">
                <AvatarImage src={a.picture} />
                <AvatarFallback className="bg-primary/20 text-primary text-xs font-semibold">{(a.name || a.email).slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{a.name || "—"}</p>
                <p className="text-xs text-muted-foreground truncate">{a.email}</p>
              </div>
              {a.user_id !== user.user_id ? (
                <Button data-testid={`demote-admin-${a.user_id}`} variant="ghost" size="sm" onClick={() => setDemoteId(a.user_id)}
                  className="text-muted-foreground hover:text-destructive h-8 px-2">
                  <UserMinus className="h-4 w-4" />
                </Button>
              ) : (
                <span className="text-[10px] uppercase tracking-wider text-primary">You</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={seedOpen} onOpenChange={(o) => { if (!o && !seedBusy) { setSeedOpen(false); setSeedResult(null); setSeedPreview(null); } }}>
        <AlertDialogContent className="bg-card border-border" data-testid="seed-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Import platform curriculum</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                {!seedPreview && !seedResult && (
                  <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Checking bundled seed…</div>
                )}
                {seedPreview && !seedResult && (
                  <>
                    <p>Bundled seed contains <b>{seedPreview.techniques.total_in_seed}</b> techniques and <b>{seedPreview.sequences.total_in_seed}</b> sequences.</p>
                    <div className="rounded-md bg-black/30 border border-border p-3 space-y-1 font-mono text-xs" data-testid="seed-preview">
                      <p>Techniques — insert: <b>{seedPreview.techniques.total_in_seed - seedPreview.techniques.skipped}</b>, already exist: <b>{seedPreview.techniques.skipped}</b></p>
                      <p>Sequences — insert: <b>{seedPreview.sequences.total_in_seed - seedPreview.sequences.skipped}</b>, already exist: <b>{seedPreview.sequences.skipped}</b></p>
                    </div>
                    <p className="text-muted-foreground">Default import will skip items that already exist. Choose &ldquo;Overwrite&rdquo; to force-update every item with the bundled version.</p>
                  </>
                )}
                {seedResult && (
                  <div className="rounded-md bg-black/30 border border-border p-3 space-y-1 font-mono text-xs" data-testid="seed-result">
                    <p>✓ Techniques — inserted <b>{seedResult.techniques.inserted}</b>, updated <b>{seedResult.techniques.updated}</b>, skipped <b>{seedResult.techniques.skipped}</b></p>
                    <p>✓ Sequences — inserted <b>{seedResult.sequences.inserted}</b>, updated <b>{seedResult.sequences.updated}</b>, skipped <b>{seedResult.sequences.skipped}</b></p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border" disabled={seedBusy} data-testid="seed-cancel-btn">{seedResult ? "Close" : "Cancel"}</AlertDialogCancel>
            {seedPreview && !seedResult && (
              <>
                <Button variant="outline" onClick={() => runSeed(true)} disabled={seedBusy} data-testid="seed-overwrite-btn" className="border-border">
                  {seedBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Overwrite all
                </Button>
                <AlertDialogAction data-testid="seed-import-btn" onClick={(e) => { e.preventDefault(); runSeed(false); }} disabled={seedBusy} className="bg-primary hover:bg-primary/90 text-white">
                  {seedBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Import (skip existing)
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!demoteId} onOpenChange={(o) => !o && setDemoteId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove platform admin?</AlertDialogTitle>
            <AlertDialogDescription>They will become a regular individual user and lose access to platform management.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-demote-admin" onClick={demote} className="bg-destructive hover:bg-destructive/90 text-white">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
